import React from 'react';

type TrendDirection = 'up' | 'down' | 'neutral';
type ColorScheme = 'primary' | 'success' | 'warning' | 'danger' | 'info';

interface StatsCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: TrendDirection;
  trendValue?: string;
  color?: ColorScheme;
  isLoading?: boolean;
}

const colorStyles: Record<ColorScheme, { bg: string; icon: string }> = {
  primary: {
    bg: 'bg-primary-50',
    icon: 'text-primary-600',
  },
  success: {
    bg: 'bg-green-50',
    icon: 'text-green-600',
  },
  warning: {
    bg: 'bg-amber-50',
    icon: 'text-amber-600',
  },
  danger: {
    bg: 'bg-red-50',
    icon: 'text-red-600',
  },
  info: {
    bg: 'bg-blue-50',
    icon: 'text-blue-600',
  },
};

const trendStyles: Record<TrendDirection, { color: string; bgColor: string }> = {
  up: {
    color: 'text-green-700',
    bgColor: 'bg-green-100',
  },
  down: {
    color: 'text-red-700',
    bgColor: 'bg-red-100',
  },
  neutral: {
    color: 'text-secondary-600',
    bgColor: 'bg-secondary-100',
  },
};

const TrendIcon: React.FC<{ direction: TrendDirection }> = ({ direction }) => {
  if (direction === 'up') {
    return (
      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
          clipRule="evenodd"
        />
      </svg>
    );
  }
  if (direction === 'down') {
    return (
      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      </svg>
    );
  }
  return (
    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z"
        clipRule="evenodd"
      />
    </svg>
  );
};

const LoadingSkeleton: React.FC = () => (
  <div className="bg-white rounded-xl border border-secondary-200 p-6 shadow-card animate-pulse">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="h-4 bg-secondary-200 rounded w-24 mb-3"></div>
        <div className="h-8 bg-secondary-200 rounded w-20 mb-3"></div>
        <div className="h-5 bg-secondary-100 rounded w-16"></div>
      </div>
      <div className="w-12 h-12 bg-secondary-100 rounded-xl"></div>
    </div>
  </div>
);

export const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  icon,
  trend,
  trendValue,
  color = 'primary',
  isLoading = false,
}) => {
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  const colors = colorStyles[color];
  const trendStyle = trend ? trendStyles[trend] : null;

  return (
    <div className="bg-white rounded-xl border border-secondary-200 p-6 shadow-card hover:shadow-soft transition-shadow duration-200">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-secondary-500 truncate">{title}</p>
          <p className="mt-2 text-3xl font-bold text-secondary-900 tracking-tight">
            {value}
          </p>
          {trend && trendValue && trendStyle && (
            <div className="mt-3 flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${trendStyle.bgColor} ${trendStyle.color}`}
              >
                <TrendIcon direction={trend} />
                {trendValue}
              </span>
              <span className="text-xs text-secondary-400">vs last month</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colors.bg}`}>
          <div className={colors.icon}>{icon}</div>
        </div>
      </div>
    </div>
  );
};

export default StatsCard;
